import type { FC } from 'react';
import classNames from 'classnames';

import GameTimer from 'app/components/GameTimer';
import JerseyIcon from 'app/icons/jersey.svg';
import FlagIcon from 'app/icons/flag.svg';

import { getSubdivisionScore, getCurrentSubdivision } from './utils';
import style from './style.module.scss';

interface Props {
  game?: MatchGame;
  opponent1: OpponentBase | undefined;
  opponent2: OpponentBase | undefined;
  slug: EBattleColumnarSlug;
  labels: string[];
}

const EBattleColumnarScoreboard: FC<Props> = ({ game, opponent1, opponent2, slug, labels }) => {
  const isRunning = game?.status === 'running';
  const isFinished = game?.status === 'finished';
  const isNotStarted = !game || game.status === 'not_started';

  const opp1 = (game?.opponents as EBattleGameOpponent[] | undefined)?.find(
    o => o.id === opponent1?.id
  );
  const opp2 = (game?.opponents as EBattleGameOpponent[] | undefined)?.find(
    o => o.id === opponent2?.id
  );

  const currentSub = getCurrentSubdivision(game, slug);

  if (isNotStarted) {
    return (
      <div className={style.scoreboard}>
        <div className={style.preMatch}>
          <div className={style.playerSide}>
            <span className={style.playerName}>{opponent1?.name}</span>
          </div>
          <span className={style.vsLabel}>vs</span>
          <div className={classNames(style.playerSide, style.rightSide)}>
            <span className={style.playerName}>{opponent2?.name}</span>
          </div>
        </div>
      </div>
    );
  }

  const gridStyle = { '--sub-count': labels.length } as React.CSSProperties;

  return (
    <div className={style.scoreboard} style={gridStyle}>
      <div className={style.columnHeaders}>
        <div className={style.timerCell}>
          {isRunning && game?.timer && (
            <GameTimer timer={game.timer} videogame={slug} live className={style.inlineTimerText} />
          )}
        </div>
        {labels.map((label, i) => {
          const subIndex = i + 1;
          const isCurrent = isRunning && currentSub === subIndex;
          return (
            <span
              key={subIndex}
              className={classNames(style.columnLabel, {
                [style.currentLabel]: isCurrent,
                [style.altColumn]: subIndex % 2 === 1
              })}
            >
              <span className={style.labelText}>
                {label}
                {isCurrent && <span className={style.liveDot} />}
              </span>
            </span>
          );
        })}
        <FlagIcon className={style.flagIcon} />
      </div>

      {[
        { opp: opp1, matchOpp: opponent1 },
        { opp: opp2, matchOpp: opponent2 }
      ].map(({ opp, matchOpp }, idx) => {
        const isWinner = isFinished && game?.winner_id === matchOpp?.id;
        const isLoser = isFinished && game?.winner_id !== null && game?.winner_id !== matchOpp?.id;

        return (
          <div key={matchOpp?.id ?? idx} className={style.playerRow}>
            <div className={style.playerInfo}>
              {opp?.ingame_team_name && (
                <span className={style.teamName}>
                  <JerseyIcon className={style.jerseyIcon} />
                  {opp.ingame_team_name}
                </span>
              )}
              <span className={style.playerName}>{matchOpp?.name}</span>
            </div>

            {labels.map((_, i) => {
              const subIndex = i + 1;
              const score = getSubdivisionScore(opp, slug, subIndex);
              const isCurrent = isRunning && currentSub === subIndex;
              const otherOpp = idx === 0 ? opp2 : opp1;
              const otherScore = getSubdivisionScore(otherOpp, slug, subIndex);
              const isLeading = score !== null && otherScore !== null && score > otherScore;

              return (
                <span
                  key={subIndex}
                  className={classNames(style.subScore, {
                    [style.currentSub]: isCurrent,
                    [style.leading]: isLeading && (isCurrent || isFinished),
                    [style.altColumn]: subIndex % 2 === 1
                  })}
                >
                  {score !== null ? score : '-'}
                </span>
              );
            })}

            <span
              className={classNames(style.totalScore, {
                [style.leading]: isWinner,
                [style.losing]: isLoser
              })}
            >
              {opp1 || opp2 ? opp?.score ?? 0 : '-'}
            </span>
          </div>
        );
      })}
    </div>
  );
};

EBattleColumnarScoreboard.displayName = 'EBattleColumnarScoreboard';

export default EBattleColumnarScoreboard;
